import { Autocomplete } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { __ANPR_PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import PmiTextField, { PmiTextFieldProps } from 'shell/components/shared/text-field';
import debounce from 'shell/utils/debounce';
import { noop } from 'shell/utils/noop';
import { Operation } from 'shell/view-models/operation';
import { DateTime } from 'luxon';

interface MunicipalityListProps extends Omit<PmiTextFieldProps, 'onChange'> {
  disabled?: boolean;
  value?: MunicipalityAnprVM | null;
  onChange: (municipality: MunicipalityAnprVM | null) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
  fromDate?: DateTime | null;
  helperText?: string;
}

export interface MunicipalityAnpr {
  codicePostazioneAnpr: string;
  dataIstituzione: string;
  dataCessazione: string;
  codiceIstat: string;
  codiceCatastale: string;
  denominazione: string;
  denominazioneTraslitterata: string;
  altraDenominazione: string;
  altraDenominazioneTraslitterata: string;
  idProvincia: string;
  idProvinciaIstat: number;
  idRegione: string;
  idPrefettura: string;
  codiceStato: string;
  codiceSiglaProvincia: string;
  fonte: string;
  dataUltimoAggiornamento: string;
  codiceDenominazione: string;
  key?: {
    codice?: number;
  };
}

export interface MunicipalityAnprVM {
  code?: number;
  id?: string;
  description: string;
  province: string;
  provinceCode?: string;
  idProvince?: number;
  startDate?: string;
  endDate?: string;
  locality?: string;
  istatCode?: string;
}

export const ID_ALTRO_COMUNE = '0';
export const LABEL_ALTRO_COMUNE = 'ALTRO COMUNE';

export const ID_ALTRA_PROVINCIA = '0';
export const LABEL_ALTRA_PROVINCIA = 'XX';

const MunicipalityListComponent = ({
  disabled,
  onChange,
  required,
  value: selectedValue = null,
  fromDate,
  error,
  label = 'Comuni',
  id = 'municipality',
  helperText,
  ...others
}: MunicipalityListProps): React.ReactElement => {
  const [municipalities, getMunicipalities] = useHttpFetch<
    HttpResponseList<MunicipalityAnpr>,
    Array<MunicipalityAnprVM>
  >(`${__ANPR_PERSON_API_URL__}/comune`, {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<MunicipalityAnpr>>) => {
      const { data } = response;

      return data?.payload.list.map((item) => ({
        id: item.key?.codice?.toString() === ID_ALTRO_COMUNE ? ID_ALTRO_COMUNE : item.codiceIstat,
        description: item.denominazione,
        provinceCode: item.idProvinciaIstat?.toString() || '',
        province: item.codiceSiglaProvincia,
        startDate: item.dataIstituzione,
        endDate: item.dataCessazione,
        code: item.key?.codice?.toString() ? item.key?.codice : undefined,
        idProvince: item.idProvincia ? Number.parseInt(item.idProvincia, 10) : undefined,
      })) as Array<MunicipalityAnprVM>;
    },
  });

  const defaultProps = useMemo(
    () => ({
      options: (municipalities.data as Array<MunicipalityAnprVM>) || [],
      getOptionLabel: (option: MunicipalityAnprVM) => {
        if (
          !option ||
          (option.id === undefined &&
            option.description === undefined &&
            option.province === undefined)
        )
          return '';
        const municipality = option.description;
        const formattedStartDate = option.startDate
          ? DateTime.fromISO(option.startDate).toFormat('dd/MM/yyyy')
          : '';
        const formattedEndDate = option.endDate
          ? DateTime.fromISO(option.endDate).toFormat('dd/MM/yyyy')
          : '';
        return `${municipality} ${
          formattedStartDate && formattedEndDate
            ? `${formattedStartDate} - ${formattedEndDate}`
            : ''
        }`;
      },
    }),
    [municipalities]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const { abort = noop } = municipalities;

        abort();

        getMunicipalities({
          params: {},
          paramsSerializer: () =>
            `filters=denominazione:${value?.trim()}:${Operation.LIKE}${
              fromDate && fromDate.isValid
                ? `&filters=dataDal:${fromDate.toFormat('yyyy-MM-dd')}`
                : ''
            }`,
        });
      },
      [municipalities.abort, getMunicipalities, fromDate]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();

        if (
          element &&
          element.id === undefined &&
          element.description === undefined &&
          element.province === undefined
        ) {
          onChange(null);
        } else {
          onChange(element);
        }
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 3 && reason === 'input') {
          handleSearch(value);
        }
      }}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id === value.id;
      }}
      clearOnBlur={false}
      loading={municipalities.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          {...others}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

MunicipalityListComponent.displayName = 'Municipality list component';

const MunicipalityList = memo(MunicipalityListComponent);

export default MunicipalityList as typeof MunicipalityListComponent;
